/**
 * Calculator Page
 *
 * TWO MODES:
 * - 'quote': Pricing upcoming work (estimates) → Save as Quote
 * - 'verdict': Logging sold work (actuals) → Save as Verdict
 *
 * Mode is passed via navigation state from NewActionMenu.
 *
 * V3: Full-screen calculator with pill accumulators, Ardy insights,
 * inline results, and sticky CTA. The CalculatorFormV3 component
 * owns its own layout — this page handles nav + persistence only.
 *
 * V4: Toast removed from save flow — NameSheet confirmation state
 * is the save feedback. Toast reserved for deletions.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CalculatorWizard } from '@/components/calculator';
import type { VerdictData } from '@/components/calculator/CalculatorWizard';
import { useAuth } from '@/contexts/AuthContext';
import { useVerdicts } from '@/hooks/useVerdicts';
import { useSubscription } from '@/hooks/useSubscription';
import { incrementPriceCheckCount } from '@/services/priceCheckCounter';
import { trackEvent } from '@/services/telemetry/telemetry';

export type CalculatorMode = 'quote' | 'verdict';

const ORIGIN_KEY = 'tp:calculator:origin';

export function CalculatorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, waitForUser } = useAuth();
  const { saveVerdict, updateVerdict, summaries } = useVerdicts();
  const { isPremium, priceChecksUsed } = useSubscription();

  // Read mode from navigation state
  const mode: CalculatorMode = (location.state as { mode?: CalculatorMode })?.mode || 'quote';

  // Capture origin on mount — prefer navigation state, fall back to sessionStorage
  useEffect(() => {
    const stateFrom = (location.state as { from?: string })?.from;
    if (stateFrom) {
      sessionStorage.setItem(ORIGIN_KEY, stateFrom);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const existingTitles = summaries
    .map(s => s.title)
    .filter((t): t is string => Boolean(t));

  // Last 3 unique dimension pairs, most recent first
  const recentDimensions = useMemo(() => {
    const seen = new Set<string>();
    const result: { w: string; h: string }[] = [];
    for (const s of summaries) {
      const key = `${s.width}×${s.height}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ w: String(s.width), h: String(s.height) });
      }
      if (result.length === 3) break;
    }
    return result;
  }, [summaries]);

  // Called by CalculatorWizard when user confirms name in NameSheet.
  // Runs Firestore write + ceremony delay in parallel, then navigates to portfolio.
  // This guarantees the save completes before navigation — no race condition.
  const handleVerdictReady = useCallback(async (verdict: VerdictData, title: string) => {
    // If user is null (optimistic auth not yet resolved), wait for real auth state.
    // This prevents the silent save failure when isAuthenticated=true but user=null.
    let resolvedUser = user;
    if (!resolvedUser) {
      console.warn('[CalculatorPage] user is null at save time — waiting for auth to resolve…');
      resolvedUser = await waitForUser();
    }
    if (!resolvedUser) {
      console.error('[CalculatorPage] Auth resolved but no user — save aborted (not logged in).');
      sessionStorage.removeItem(ORIGIN_KEY);
      navigate('/app/portfolio', { replace: true });
      return;
    }

    try {
      console.log('[CalculatorPage] Starting save for:', title, 'uid:', resolvedUser.uid);

      // Run save and ceremony timer in parallel — navigate after BOTH complete
      const ceremonyDelay = new Promise(resolve => setTimeout(resolve, 1800));

      const [saved] = await Promise.all([
        saveVerdict(
          { ...verdict.inputs, title },
          verdict.calculations,
          verdict.flags,
        ),
        ceremonyDelay,
      ]);

      if (saved) {
        console.log('[CalculatorPage] Save succeeded, id:', saved.id);
        // Reverse trial: if this was the user's first ever Price Check on free, mark it
        const isFirstFreeCheck = !isPremium && priceChecksUsed === 0;
        if (isFirstFreeCheck) {
          updateVerdict(saved.id, { hadFreeRoast: true }).catch(() => {});
        }
        // Increment lifetime Price Check counter (best-effort, never blocks)
        incrementPriceCheckCount(resolvedUser.uid).catch(() => {});
        trackEvent({
          eventType: 'price_check_saved',
          screen: 'calculator',
          meta: {
            checkNumber: priceChecksUsed + 1,
            isPremium,
            hadRoast: isFirstFreeCheck || isPremium,
          },
        });
        const currentVersion = parseInt(sessionStorage.getItem('verdictsVersion') || '0', 10);
        sessionStorage.setItem('verdictsVersion', String(currentVersion + 1));
      } else {
        console.error('[CalculatorPage] saveVerdict returned null — Firestore write failed');
      }

      sessionStorage.removeItem(ORIGIN_KEY);
      navigate('/app/portfolio', {
        replace: true,
        state: saved ? { openVerdictId: saved.id } : undefined,
      });
    } catch (err) {
      console.error('[CalculatorPage] handleVerdictReady crashed:', err);
      sessionStorage.removeItem(ORIGIN_KEY);
      navigate('/app/portfolio', { replace: true });
    }
  }, [user, waitForUser, saveVerdict, navigate, isPremium, priceChecksUsed, updateVerdict]);

  return (
    <CalculatorWizard
      mode={mode}
      onVerdictReady={handleVerdictReady}
      existingTitles={existingTitles}
      recentDimensions={recentDimensions}
      onBack={() => navigate(-1)}
    />
  );
}

export default CalculatorPage;
